import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { 
  RefreshCw, 
  Search, 
  Building2, 
  ArrowRight,
  ArrowLeft,
  Loader2,
  FileSearch,
  CheckCircle,
  Clock,
  UserPlus,
  User,
  Users,
  ChevronDown,
  ChevronUp,
  Circle,
  FileText,
  CreditCard,
  Phone,
  Database,
  Calculator,
  Banknote,
  Eye,
  X,
  Calendar,
  ExternalLink,
  MessageCircle,
  Paperclip,
  Wifi,
  WifiOff,
  Bell,
  Activity,
  ArrowLeftRight,
  XCircle,
  PoundSterling,
  ShieldCheck
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PageHeader } from "@/components/PageHeader";
import { CostOfFundingHover, CostOfSalesHover } from "@/components/CostOfFundingHover";
import { CheckBalanceButton } from "@/components/PayoutBalanceCard";
import { StatusBadge, CountryBadge, CurrencyBadge } from "@/components/StatusBadge";
import { 
  getWorkflowItems, 
  syncWorkflowItems,
  getMondayItemUpdates,
  getOverviewStats, 
  assignApplication, 
  getDirectory,
  updateWorkflowItemStatus,
  assignFundingSpecialist,
  getFundingSpecialist,
  getFundedApplications, patchSalesMetrics,
  syncMondayFundedDeals, getFundedSyncState
} from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { DocuSignEnvelopesPanel } from "@/pages/legal/DocuSignEnvelopesPanel";
import DocumentGeneration from "@/pages/legal/DocumentGeneration";
import { useWorkflowWebSocket } from "@/hooks/useWorkflowWebSocket";
import { toast } from "sonner";
import { sanitizedHtmlProps } from '@/lib/sanitizeEmailHtml';

// SimplyFunded brand blue color
const SF_BLUE = "#3B70E0";

// Sales ask (Sep 2026): inline-editable comms / cost-of-funding cell on the Funded table
const SalesMetricCell = ({ app, field, curSym, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const current = field === "commission_amount" ? app.commission_amount : app.cost_of_funding;
  const missing = current === null || current === undefined || current === "";
  const save = async () => {
    setBusy(true);
    try {
      const body = field === "commission_amount" ? { commission_amount: Number(val) } : { cost_of_funding_override: Number(val) };
      await patchSalesMetrics(app.application_id || app.id, body);
      toast.success(field === "commission_amount" ? "Commission updated" : "Cost of funding updated");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error("Update failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input type="number" autoFocus value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          className="h-6 w-24 border rounded px-1 text-right text-xs" data-testid={`sales-metric-input-${field}-${app.application_id}`} />
        <button type="button" onClick={save} disabled={busy} className="text-emerald-700 text-[10px] font-semibold" data-testid={`sales-metric-save-${field}-${app.application_id}`}>{busy ? "…" : "Save"}</button>
        <button type="button" onClick={() => setEditing(false)} className="text-slate-400 text-[10px]">✕</button>
      </span>
    );
  }
  return (
    <button type="button" onClick={() => { setVal(missing ? "" : Number(current)); setEditing(true); }}
      className={`tabular-nums hover:underline ${missing ? "text-amber-700 italic" : "text-slate-700"}`}
      title={field === "commission_amount"
        ? `${app.commission_percent != null ? app.commission_percent + "% · " : ""}${app.commission_source === "portal" ? "edited in portal" : "from Monday"} — click to correct`
        : (missing ? "Disbursement not processed in the portal — click to enter the amount disbursed"
          : app.cost_of_funding_parts?.source === "portal_override" ? "Entered in the portal — click to change"
          : app.cost_of_funding_parts?.source === "loandisk_ledger"
            ? `From the LoanDisk ledger: principal ${curSym}${Number(app.cost_of_funding_parts?.principal ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${Number(app.cost_of_funding_parts?.renewal_settled || 0) > 0 ? ` less Renewal settlement ${curSym}${Number(app.cost_of_funding_parts.renewal_settled).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${(app.cost_of_funding_parts.renewal_from || []).join(", ")})` : ""}${(app.cost_of_funding_parts?.flags || []).length ? ` · ⚠ ${app.cost_of_funding_parts.flags.join(" · ")}` : ""} — click to override`
          : `Nominal ${curSym}${Number(app.cost_of_funding_parts?.nominal ?? 0).toFixed(2)} + final disbursement ${curSym}${Number(app.cost_of_funding_parts?.disbursement ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${app.cost_of_funding_parts?.ledger_check && !app.cost_of_funding_parts.ledger_check.matches ? ` · ⚠ LoanDisk ledger says ${curSym}${Number(app.cost_of_funding_parts.ledger_check.ledger_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""} — click to override`)}
      data-testid={`sales-metric-${field}-${app.application_id}`}>
      {missing ? (field === "commission_amount" ? "add" : "—")
        : field === "commission_amount" ? `${curSym}${Math.round(Number(current)).toLocaleString()}`
        : <>{`${curSym}${Number(current).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}{app.cost_of_funding_parts?.source === "loandisk_ledger" && <span className={`ml-1 text-[9px] font-semibold ${(app.cost_of_funding_parts.flags || []).length ? "text-amber-600" : "text-slate-400"}`} data-testid={`cof-ledger-badge-${app.application_id}`}>LD</span>}</>}
    </button>
  );
};

// Credit Approved Deals specific groups (only these two)
const CREDIT_APPROVED_GROUPS = {
  "new_group_mkmkc789": { name: "Ready for Doc's", color: "#ff158a" },
  "new_group55967__1": { name: "Funded", color: "#c4b4fc" }
};

// Groups that items can be sent back to (from Applications Workflow)
// These are STATUS values that trigger Monday.com automations to move groups
const SEND_BACK_STATUSES = [
  { id: "Application", name: "Application", color: "#fdab3d" },
  { id: "Processing", name: "Processing", color: "#00c875" },
  { id: "UW", name: "UW (Underwriting)", color: "#0073ea" },
  { id: "UW - IR", name: "UW - Info Requested", color: "#00d2d2" },
  { id: "Info Requested", name: "Info Requested", color: "#fdab3d" },
  { id: "Hold", name: "Hold", color: "#c4c4c4" },
  { id: "Conditional Offer", name: "Conditional Offer", color: "#ff158a" },
  { id: "Offer Accepted", name: "Offer Accepted", color: "#037f4c" },
  { id: "Final Checks", name: "Final Checks", color: "#175a63" },
  { id: "Funded", name: "Funded", color: "#c4b4fc" },
];

// Helper to get workflow step status from workflow item data
const getStepStatus = (app, stepKey) => {
  if (!app) return "pending";
  
  // Map workflow item status/columns to step status
  const status = app.status?.toLowerCase() || "";
  
  switch (stepKey) {
    case "contract_created":
      if (app.contract_generated) return "done";
      // Docs can only be out/signed if they were created first
      if (app.docusign_status || (status.includes("doc") && status.includes("out"))) return "done";
      if (status.includes("raise") && status.includes("doc")) return "working";
      return "todo";
    case "docusign":
      if (app.docusign_status === "signed" || app.docusign_status === "completed") return "done";
      if (app.docusign_status === "sent" || app.docusign_status === "delivered" || app.docusign_status === "viewed") return "working";
      if (status.includes("doc") && status.includes("out")) return "working";
      return "todo";
    case "fraud_check":
      return app.docusign_fraud_checked_at ? "done" : "todo";
    case "ch_verify":
      return app.ch_verification_status === "verified" ? "done" : "todo";
    case "bank_cop":
      return app.bank_details_status === "verified" ? "done" : 
             app.bank_details_status === "pending_approval" ? "working" : "todo";
    case "standing_order":
      return app.standing_order_initiated ? "done" : "todo";
    case "loandisk":
      return app.loandisk_validated ? "done" : 
             app.loandisk_loan_id ? "working" : "todo";
    case "net_in_hand":
      return app.net_in_hand_confirmed ? "done" :
             app.net_in_hand_approval_pending ? "working" : "todo";
    case "nominal":
      return app.test_payment_confirmed ? "done" :
             app.test_payment_sent ? "working" : "todo";
    case "prefunding":
      if (app.prefunding_call_completed) return "done";
      return app.prefunding_call_failed ? "failed" : "todo";
    case "disbursement":
      // If in Funded group, disbursement is done
      if (app.group_id === "new_group55967__1") return "done";
      if (status.includes("funded")) return "done";
      return app.disbursement_confirmed ? "done" : 
             (app.test_payment_confirmed && app.prefunding_call_completed &&
              (!app.is_topup || app.net_in_hand_confirmed)) ? "working" : "todo";
    default:
      return "todo";
  }
};

// Status indicator component
const StatusIndicator = ({ status }) => {
  if (status === "done") {
    return <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />;
  }
  if (status === "working") {
    return <Clock className="w-3.5 h-3.5 text-amber-500" />;
  }
  if (status === "failed") {
    return <XCircle className="w-3.5 h-3.5 text-red-500" />;
  }
  return <Circle className="w-3.5 h-3.5 text-slate-300" />;
};

// Workflow checklist dropdown component
const WorkflowChecklist = ({ app, region = "UK" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isTopUp = app?.is_topup || app?.loan_type === "TopUp" || app?.name?.includes("(TU)");
  const aus = region === "AUS";
  
  const steps = [
    { key: "contract_created", label: "Contracts Created", icon: FileText },
    { key: "docusign", label: "DocuSign Signed", icon: FileText },
    { key: "fraud_check", label: "Fraud Check", icon: ShieldCheck },
    { key: "ch_verify", label: aus ? "ABN / ASIC Verify" : "Companies House Verify", icon: Building2 },
    { key: "bank_cop", label: aus ? "Bank Details Verified" : "Bank CoP Verified", icon: CreditCard },
    { key: "standing_order", label: aus ? "Direct Debit (GC BECS)" : "Standing Order (Initiate Payment)", icon: Banknote },
    { key: "loandisk", label: "Add Loan to LoanDisk", icon: Database },
    ...(isTopUp ? [{ key: "net_in_hand", label: "Net in Hand", icon: Calculator }] : []),
    { key: "nominal", label: "Nominal (Test) Payment", icon: PoundSterling },
    { key: "prefunding", label: "Pre-Funding Call", icon: Phone },
    { key: "disbursement", label: "Disburse Loan", icon: CheckCircle },
  ];
  
  const completedCount = steps.filter(s => getStepStatus(app, s.key) === "done").length;
  const workingCount = steps.filter(s => getStepStatus(app, s.key) === "working").length;
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors py-1">
          <span className="flex items-center gap-1.5">
            <span className="text-emerald-600 font-medium">{completedCount}</span>
            <span>/</span>
            <span>{steps.length}</span>
            <span className="text-slate-400">complete</span>
            {workingCount > 0 && (
              <span className="text-amber-600 ml-1">({workingCount} in progress)</span>
            )}
          </span>
          {isOpen ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pt-2 space-y-1.5 border-t mt-1">
          {steps.map((step) => {
            const status = getStepStatus(app, step.key);
            const Icon = step.icon;
            return (
              <div 
                key={step.key} 
                className={`flex items-center gap-2 text-xs py-0.5 ${
                  status === "done" ? "text-emerald-700" : 
                  status === "working" ? "text-amber-700" : 
                  status === "failed" ? "text-red-700" : "text-slate-500"
                }`}
              >
                <StatusIndicator status={status} />
                <Icon className="w-3 h-3" />
                <span>{step.label}</span>
                <span className={`ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded ${
                  status === "done" ? "bg-emerald-100 text-emerald-700" : 
                  status === "working" ? "bg-amber-100 text-amber-700" : 
                  status === "failed" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"
                }`}>
                  {status === "done" ? "Done" : status === "working" ? "Working" : status === "failed" ? "Failed" : "To Do"}
                </span>
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Item Card Component - Moved outside to avoid re-render issues
const ItemCard = ({ 
  app, 
  isFunded = false, 
  onOpenModal, 
  onAssign, 
  onOpenAssignDialog,
  onSendBack,
  sendingBack,
  canAssignAnyone,
  user,
  assigning,
  navigate,
  envelopeSummary = null,
  onLaunchWizard = null,
  currency = "£",
  region = "UK",
}) => {
  const [showSendBackMenu, setShowSendBackMenu] = useState(false);
  const isTopUp = app?.name?.includes("(TU)") || app?.is_topup;
  const groupColor = isFunded ? CREDIT_APPROVED_GROUPS["new_group55967__1"].color : CREDIT_APPROVED_GROUPS["new_group_mkmkc789"].color;
  
  return (
    <Card 
      className="deal-card hover:shadow-md transition-shadow cursor-pointer"
      style={{ borderLeft: `4px solid ${groupColor}` }}
      onClick={() => onOpenModal(app)}
      data-testid={`deal-card-${app.sf_number || app.monday_item_id}`}
    >
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${groupColor}20` }}>
              <Building2 className="w-4 h-4" style={{ color: groupColor }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <CardTitle className="text-sm font-semibold truncate">
                  {app.name?.replace(/^SFA?-\d+\s*-?\s*/, '') || app.sf_number}
                </CardTitle>
                {isTopUp ? (
                  <span className="inline-flex items-center rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-800 flex-shrink-0">
                    TU
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white flex-shrink-0" style={{ backgroundColor: SF_BLUE }}>
                    New
                  </span>
                )}
                {app.gc_collection_status && (
                  <span
                    className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold flex-shrink-0 ${
                      app.gc_collection_status === "link_created"
                        ? "bg-amber-100 text-amber-800"
                        : ["mandate_active", "cycle_active"].includes(app.gc_collection_status)
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-red-100 text-red-700"
                    }`}
                    title={`GoCardless ${(app.gc_collection_method || "dd").toUpperCase()}: ${app.gc_collection_status.replace(/_/g, " ")}`}
                    data-testid="gc-invite-badge"
                  >
                    {(app.gc_collection_method || "dd").toUpperCase()}{" "}
                    {app.gc_collection_status === "link_created" ? "invite sent"
                      : app.gc_collection_status === "mandate_active" ? "subscribed"
                      : app.gc_collection_status === "cycle_active" ? "live"
                      : app.gc_collection_status.replace(/_/g, " ")}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground font-mono">{app.sf_number}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {/* Status badge */}
            <span 
              className="text-[10px] px-2 py-0.5 rounded text-white font-medium"
              style={{ backgroundColor: groupColor }}
            >
              {app.status || (isFunded ? "Funded" : "Ready")}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 px-4 pb-4 pt-0">
        {/* Loan Amount */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Loan Amount</span>
          <span className="font-bold" style={{ color: groupColor }}>
            {currency}{(app.loan_amount || 0).toLocaleString()}
          </span>
        </div>

        {/* Broker */}
        {app.broker_name && (
          <div className="text-xs text-muted-foreground truncate">
            Broker: <span className="font-medium text-foreground">{app.broker_name}</span>
            {app.commission && (
              <span className="ml-1">({app.commission}%)</span>
            )}
          </div>
        )}

        {/* Contact */}
        {app.contact_name && (
          <div className="text-xs text-muted-foreground truncate">
            Contact: <span className="font-medium text-foreground">{app.contact_name}</span>
          </div>
        )}

        {/* Workflow Status Dropdown */}
        <div className="pt-1" onClick={(e) => e.stopPropagation()}>
          <WorkflowChecklist app={app} region={region} />
        </div>

        {/* Underwriter (Read-only) & Funding Specialist (Assignable) */}
        <div className="pt-2 border-t space-y-2" onClick={(e) => e.stopPropagation()}>
          {/* Underwriter - Read Only */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs">
              <User className="w-3 h-3 text-blue-500" />
              <span className="text-muted-foreground">UW:</span>
              <span className="font-medium truncate max-w-[80px]">
                {app.account_manager || "Not assigned"}
              </span>
            </div>
          </div>
          
          {/* Funding Specialist - Assignable */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs">
              <Users className="w-3 h-3 text-pink-500" />
              <span className="text-muted-foreground">Funding:</span>
              {app.funding_specialist ? (
                <>
                  <span className="font-medium truncate max-w-[80px]">{app.funding_specialist}</span>
                  {app.funding_specialist === user?.name && (
                    <span className="text-[10px] bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded-full">You</span>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground italic">Not assigned</span>
              )}
            </div>
            {canAssignAnyone ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-xs text-pink-600 hover:text-pink-700 hover:bg-pink-50"
                onClick={(e) => onOpenAssignDialog(app, e, 'funding_specialist')}
                disabled={assigning === app.monday_item_id}
              >
                {app.funding_specialist ? <RefreshCw className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}
              </Button>
            ) : !app.funding_specialist && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-xs text-pink-600 hover:text-pink-700 hover:bg-pink-50"
                onClick={(e) => onAssign(app, e, 'funding_specialist')}
                disabled={assigning === app.monday_item_id}
              >
                {assigning === app.monday_item_id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <UserPlus className="w-3 h-3" />
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {/* Change Status Button - syncs status to Monday, automation moves the group */}
          <Popover open={showSendBackMenu} onOpenChange={setShowSendBackMenu}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="flex-1 h-8 text-xs"
                onClick={(e) => e.stopPropagation()}
                disabled={sendingBack === app.monday_item_id}
                data-testid={`change-status-btn-${app.monday_item_id}`}
              >
                {sendingBack === app.monday_item_id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <ArrowLeftRight className="w-3 h-3 mr-1" />
                    Change Status
                  </>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="start" onClick={(e) => e.stopPropagation()}>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground px-2 py-1">Change status to:</p>
                {SEND_BACK_STATUSES.map((status) => (
                  <button
                    key={status.id}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-slate-100 transition-colors text-left"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSendBack(app, status.id, status.name);
                      setShowSendBackMenu(false);
                    }}
                  >
                    <div 
                      className="w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: status.color }}
                    />
                    <span className="truncate">{status.name}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* Process Button */}
          <Button
            className="flex-1 h-8 text-xs hover:opacity-90"
            style={{ backgroundColor: groupColor }}
            onClick={(e) => {
              e.stopPropagation();
              const appIdForWorkflow = app.sf_number || app.application_id || app.monday_item_id;
              navigate(`/workflow/${appIdForWorkflow}`);
            }}
            data-testid={`process-btn-${app.monday_item_id}`}
          >
            {isFunded ? "View" : "Process"}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>

        {/* Launch Onboarding Centre — hands off to the legal team's wizard
            with this deal pre-selected. Shown for Ready for Doc's only. */}
        {!isFunded && (
          <div onClick={(e) => e.stopPropagation()} className="pt-1">
            {envelopeSummary?.status && (
              <div className="mb-1.5 flex items-center justify-between text-[11px] rounded border px-2 py-1 bg-slate-50 border-slate-200">
                <span className="text-slate-600">DocuSign:</span>
                <span
                  className={`font-semibold ${
                    ["completed", "signed"].includes(envelopeSummary.status)
                      ? "text-emerald-700"
                      : ["sent", "delivered"].includes(envelopeSummary.status)
                      ? "text-blue-700"
                      : "text-amber-700"
                  }`}
                  data-testid={`envelope-status-${app.monday_item_id}`}
                >
                  {envelopeSummary.status}
                  {envelopeSummary.offer_pdf_attached && " · offer PDF"}
                </span>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="flex-1 min-w-[185px] h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={(e) => {
                  e.stopPropagation();
                  const dealId = app.sf_number || app.application_id || app.monday_item_id;
                  if (region === "AUS") navigate(`/aus/onboarding-centre?sfa=${encodeURIComponent(dealId)}&company=${encodeURIComponent(app.company_name || app.name || "")}`);
                  else if (onLaunchWizard) onLaunchWizard(dealId);
                  else navigate(`/legal/onboarding-centre?deal=${encodeURIComponent(dealId)}`);
                }}
                data-testid={`launch-contract-wizard-${app.monday_item_id}`}
                title="Open the contract wizard with this deal pre-filled (pops out here — no page change)"
              >
                <FileText className="w-3 h-3 mr-1 shrink-0" />
                <span className="truncate">{envelopeSummary?.envelope_id ? "Re-open Contract Wizard" : "Launch Contract Wizard"}</span>
              </Button>
              <Button
                variant="outline"
                className="flex-1 min-w-[165px] h-8 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(region === "AUS" ? "/aus/onboarding-centre" : "/legal/onboarding-centre");
                }}
                data-testid={`go-onboarding-centre-${app.monday_item_id}`}
                title="Open the Onboarding Centre"
              >
                <ExternalLink className="w-3 h-3 mr-1 shrink-0" />
                <span className="truncate">Onboarding Centre</span>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function ApplicationSearch() {
  const navigate = useNavigate();
  const { user, canAssignAnyone } = useAuth();
  const { currentRegion } = useRegion();
  const isAus = currentRegion === "AUS";
  const curSym = isAus ? "A$" : "£";
  
  // Items from workflow (Ready for Doc's group only - Funded uses separate collection)
  const [readyForDocsItems, setReadyForDocsItems] = useState([]);
  const [filteredReadyForDocs, setFilteredReadyForDocs] = useState([]);
  // Map of SF# → { envelope_id, status, sent_at, offer_pdf_attached }
  // Powers the "DocuSign sent" status pill on each card so the credit team
  // sees at a glance which deals have already moved into legal.
  const [envelopeSummaries, setEnvelopeSummaries] = useState({});
  
  // Funded applications (from funded_applications collection with monthly breakdown)
  const [fundedApps, setFundedApps] = useState([]);
  const [fundedMonthlySummary, setFundedMonthlySummary] = useState([]);
  const [selectedFundedYear, setSelectedFundedYear] = useState(new Date().getFullYear()); // Default to the latest year
  const [selectedFundedMonth, setSelectedFundedMonth] = useState(null);
  const [fundedLoading, setFundedLoading] = useState(false);
  const [fundedStats, setFundedStats] = useState({ total_count: 0, total_amount: 0, new_deals: 0, topups: 0 });
  const [fundedSearchQuery, setFundedSearchQuery] = useState("");
  const [filteredFunded, setFilteredFunded] = useState([]);
  const [availableYears] = useState(() => {
    const latest = new Date().getFullYear();
    const years = [];
    for (let y = 2024; y <= latest; y++) years.push(y);
    return years;
  });

  // Trimmed DocuSign envelopes panel (same as Onboarding Centre) — lazy-loaded on first expand
  const [docusignEnvelopes, setDocusignEnvelopes] = useState([]);
  const [loadingEnvelopes, setLoadingEnvelopes] = useState(false);
  const [envelopesError, setEnvelopesError] = useState(null);
  const [envelopesLoaded, setEnvelopesLoaded] = useState(false);

  const loadDocuSignEnvelopes = async () => {
    try {
      setLoadingEnvelopes(true);
      setEnvelopesError(null);
      const response = await api.get('/contracts/envelopes/list', { params: { region: isAus ? 'aus' : 'uk', days: 90 } });
      if (response.data?.success) {
        setDocusignEnvelopes(response.data.envelopes || []);
        setEnvelopesLoaded(true);
      } else {
        setEnvelopesError(response.data?.error || "Could not load envelopes");
      }
    } catch (error) {
      setEnvelopesError(error?.response?.data?.detail || "Could not load DocuSign envelopes");
    } finally {
      setLoadingEnvelopes(false);
    }
  };

  useEffect(() => {
    loadDocuSignEnvelopes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRegion]);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const handleBackfillCoF = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post(`/applications/funded/backfill-cost-of-funding?since=2026-08-01&region=${currentRegion}`);
      const r = data?.regions?.[isAus ? "AUS" : "UK"] || {};
      toast.success(`Cost of funding recalculated: ${r.filled ?? 0} of ${r.rows ?? 0} deals since 1 Aug · ${r.missing ?? 0} not processed in the portal${r.ledger_sync_started ? " · LoanDisk ledger refresh started — reload in a few minutes" : ""}`);
      loadFundedApplications(selectedFundedYear, selectedFundedMonth);
    } catch (e) {
      toast.error("Recalculation failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBackfilling(false); }
  };
  const [stats, setStats] = useState(null);
  const [assigning, setAssigning] = useState(null);
  
  // Item detail modal
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [modalTab, setModalTab] = useState("details");
  const [mondayUpdates, setMondayUpdates] = useState([]);
  const [mondayUpdatesLoading, setMondayUpdatesLoading] = useState(false);
  const [mondayColumns, setMondayColumns] = useState({});
  
  // Assignment dialog state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedAppForAssign, setSelectedAppForAssign] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  
  // Activity feed for real-time updates
  const [activityFeed, setActivityFeed] = useState([]);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [unreadActivityCount, setUnreadActivityCount] = useState(0);
  
  // Send back state
  const [sendingBack, setSendingBack] = useState(null);
  
  // Contract wizard pop-out (renders the Onboarding Centre wizard in-place)
  const [wizardDealId, setWizardDealId] = useState(null);
  
  // Assignment type tracking (null = none, 'funding_specialist' = assigning funding specialist)
  const [assignmentType, setAssignmentType] = useState(null);

  // Add activity to feed
  const addActivity = useCallback((activity) => {
    setActivityFeed(prev => {
      const newFeed = [{
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...activity
      }, ...prev].slice(0, 50);
      return newFeed;
    });
    setUnreadActivityCount(prev => prev + 1);
  }, []);

  // Handle real-time WebSocket updates (only for Ready for Doc's group)
  const handleWebSocketUpdate = useCallback((data) => {
    if (isAus) return; // live board webhooks are wired to the UK board only
    console.log("🔄 Credit Approved - WebSocket update received:", data);
    
    if (data.type === "item_updated" && data.data?.item) {
      const updatedItem = data.data.item;
      const newGroupId = updatedItem.group_id;
      const newGroupName = data.data.group_name || CREDIT_APPROVED_GROUPS[newGroupId]?.name || newGroupId;
      
      // Only process Ready for Doc's group (Funded uses separate collection)
      const isInReadyForDocs = newGroupId === "new_group_mkmkc789";
      
      console.log(`📦 Item ${updatedItem.monday_item_id} (${updatedItem.name}) -> Group: ${newGroupName} (In Ready for Docs: ${isInReadyForDocs})`);
      
      // Check if item was previously in Ready for Doc's
      let wasInReadyForDocs = false;
      
      setReadyForDocsItems(prev => {
        const idx = prev.findIndex(item => item.monday_item_id === updatedItem.monday_item_id);
        if (idx >= 0) {
          wasInReadyForDocs = true;
          // Remove from Ready for Docs
          const newItems = prev.filter(item => item.monday_item_id !== updatedItem.monday_item_id);
          // If still in Ready for Docs, add back with updated data
          if (isInReadyForDocs) {
            return [updatedItem, ...newItems.filter(item => item.monday_item_id !== updatedItem.monday_item_id)];
          }
          return newItems;
        }
        // Item wasn't in Ready for Docs, but now is
        if (isInReadyForDocs) {
          return [updatedItem, ...prev];
        }
        return prev;
      });
      
      // Add to activity feed if relevant
      if (isInReadyForDocs || wasInReadyForDocs) {
        const toGroup = isInReadyForDocs ? "Ready for Doc's" : "Other Stage";
        addActivity({
          type: 'item_updated',
          itemId: updatedItem.monday_item_id,
          sfNumber: updatedItem.sf_number,
          itemName: updatedItem.name,
          status: updatedItem.status,
          toGroup,
          source: 'monday'
        });
      }
    }
  }, [addActivity, isAus]);

  // WebSocket connection for real-time updates
  const { isConnected, connectionCount } = useWorkflowWebSocket(handleWebSocketUpdate);
  
  // Auto-refresh interval as fallback for unreliable webhooks
  const [lastAutoRefresh, setLastAutoRefresh] = useState(null);
  const [quickRefreshing, setQuickRefreshing] = useState(false);
  const [refreshCountdown, setRefreshCountdown] = useState(60);
  const AUTO_REFRESH_INTERVAL = 60; // seconds — quick Monday pull for Ready for Doc's

  // Load Ready for Doc's items from workflow_items collection
  const loadWorkflowItems = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      // Fetch Ready for Doc's items only
      const readyResult = await getWorkflowItems("new_group_mkmkc789", null, null, 200, 0, currentRegion);
      setReadyForDocsItems(readyResult.items || []);
      setFilteredReadyForDocs(readyResult.items || []);
      
      if (silent) {
        setLastAutoRefresh(new Date());
      }
      
    } catch (error) {
      console.error("Failed to load workflow items:", error);
      if (!silent) toast.error("Failed to load applications");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentRegion]);

  // Load funded applications from funded_applications collection
  const loadFundedApplications = useCallback(async (year = null, month = null) => {
    try {
      setFundedLoading(true);
      // Use provided year or default to selectedFundedYear state
      const targetYear = year !== null ? year : selectedFundedYear;
      const data = await getFundedApplications(targetYear, month, currentRegion);
      
      setFundedApps(data.applications || []);
      setFilteredFunded(data.applications || []);
      setFundedMonthlySummary(data.monthly_summary || []);
      
      // Calculate stats
      const apps = data.applications || [];
      const newDeals = apps.filter(a => !a.company_name?.includes('(TU)')).length;
      const topups = apps.filter(a => a.company_name?.includes('(TU)')).length;
      const totalAmount = apps.reduce((sum, a) => sum + (a.loan_amount || 0), 0);
      
      setFundedStats({
        total_count: apps.length,
        total_amount: totalAmount,
        new_deals: newDeals,
        topups: topups,
        sales: data.sales_metrics || null,
      });
      
    } catch (error) {
      console.error("Failed to load funded applications:", error);
      toast.error("Failed to load funded applications");
    } finally {
      setFundedLoading(false);
    }
  }, [selectedFundedYear, currentRegion]);

  // Sync funded deals from Monday.com — incremental by default (changes since the last sync; auto every 15 min),
  // full re-sync on request. Dean (12 Oct 2026): the old button walked every funded deal each press.
  const [fundedSyncState, setFundedSyncState] = useState(null);
  const loadFundedSyncState = useCallback(() => {
    getFundedSyncState(currentRegion).then(setFundedSyncState).catch(() => {});
  }, [currentRegion]);
  useEffect(() => { loadFundedSyncState(); }, [loadFundedSyncState]);
  const handleSyncFunded = async (mode = "auto") => {
    try {
      setSyncing(true);
      const r = await syncMondayFundedDeals(currentRegion, mode);
      const secs = Math.max(1, Math.round((r.duration_ms || 0) / 1000));
      const changes = `${r.created || 0} new · ${r.updated || 0} updated${r.left_funded ? ` · ${r.left_funded} left Funded` : ""}`;
      toast.success(r.mode === "full"
        ? `Full re-sync done — ${r.scanned} funded deals checked (${changes}) in ${secs}s`
        : `Funded deals up to date — ${changes} since the last sync (${r.changed || 0} changed in Monday, ${secs}s)`);
      if (r.state) setFundedSyncState(r.state);
      loadFundedApplications(selectedFundedYear, selectedFundedMonth);
    } catch (error) {
      console.error("Failed to sync funded deals:", error);
      toast.error(error.response?.data?.detail || "Failed to sync funded deals");
    } finally {
      setSyncing(false);
    }
  };

  // Quick refresh: pulls JUST the Ready for Doc's group straight from Monday
  // (fast) — the full "Sync Monday" button is untouched.
  const quickRefreshReadyForDocs = useCallback(async (silent = false) => {
    try {
      if (!silent) setQuickRefreshing(true);
      await api.post(`/contracts/monday/refresh-ready-for-docs?region=${currentRegion}`);
      await loadWorkflowItems(true);
      setLastAutoRefresh(new Date());
      if (!silent) toast.success("Ready for Doc's refreshed from Monday");
    } catch (error) {
      console.error("Quick refresh failed:", error);
      if (!silent) toast.error("Quick refresh failed");
    } finally {
      setQuickRefreshing(false);
    }
  }, [loadWorkflowItems]);

  // 60s auto-refresh with countdown (like the inbox)
  useEffect(() => {
    const tick = setInterval(() => {
      setRefreshCountdown((prev) => {
        if (prev <= 1) {
          quickRefreshReadyForDocs(true);
          return AUTO_REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [quickRefreshReadyForDocs]);

  useEffect(() => {
    loadWorkflowItems();
    loadFundedApplications();
    loadStats();
    // Load team members if user can assign anyone
    if (canAssignAnyone && canAssignAnyone()) {
      loadTeamMembers();
    }
  }, [loadWorkflowItems, loadFundedApplications, canAssignAnyone]);

  // Filter Ready for Docs items by search
  useEffect(() => {
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      setFilteredReadyForDocs(readyForDocsItems.filter(app =>
        app.name?.toLowerCase().includes(query) ||
        app.sf_number?.toLowerCase().includes(query) ||
        app.broker_name?.toLowerCase().includes(query)
      ));
    } else {
      setFilteredReadyForDocs(readyForDocsItems);
    }
  }, [searchTerm, readyForDocsItems]);

  // Filter Funded items by search
  useEffect(() => {
    if (fundedSearchQuery) {
      const query = fundedSearchQuery.toLowerCase();
      setFilteredFunded(fundedApps.filter(app =>
        app.company_name?.toLowerCase().includes(query) ||
        app.application_id?.toLowerCase().includes(query) ||
        app.broker_name?.toLowerCase().includes(query)
      ));
    } else {
      setFilteredFunded(fundedApps);
    }
  }, [fundedSearchQuery, fundedApps]);

  // Whenever the Ready-for-Docs list changes, fetch DocuSign envelope status
  // for each deal so the cards can show "envelope sent / signed" pills.
  useEffect(() => {
    const ids = readyForDocsItems
      .map((a) => a.sf_number || a.application_id)
      .filter(Boolean);
    if (ids.length === 0) {
      setEnvelopeSummaries({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { default: apiClient } = await import("@/lib/api");
        const { data } = await apiClient.get("/contracts/envelopes-summary", {
          params: { application_ids: ids.join(",") },
        });
        if (!cancelled) setEnvelopeSummaries(data?.summaries || {});
      } catch (err) {
        // Non-fatal — cards just won't show the pill
        console.warn("Envelope summary fetch failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readyForDocsItems]);

  // Handle month selection for funded
  const handleMonthSelect = (month) => {
    setSelectedFundedMonth(month);
    loadFundedApplications(selectedFundedYear, month);
  };

  // Handle year selection for funded
  const handleYearSelect = (year) => {
    setSelectedFundedYear(year);
    setSelectedFundedMonth(null); // Reset month when year changes
    loadFundedApplications(year, null);
  };

  const loadTeamMembers = async () => {
    try {
      setLoadingTeam(true);
      const users = await getDirectory();
      setTeamMembers(users);
    } catch (error) {
      console.error("Failed to load team members:", error);
    } finally {
      setLoadingTeam(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await getOverviewStats();
      setStats(data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  // Sync from Monday.com
  const handleSync = async () => {
    try {
      setSyncing(true);
      const result = await syncWorkflowItems("2025-04-01", currentRegion);
      toast.success(`Synced ${result.total} items (${result.created} new, ${result.updated} updated)`);
      loadWorkflowItems();
    } catch (error) {
      console.error("Sync failed:", error);
      toast.error("Failed to sync from Monday.com");
    } finally {
      setSyncing(false);
    }
  };

  // Send item back by changing status (Monday.com automation will move the group)
  const handleSendBack = async (app, targetStatus, targetStatusName) => {
    if (!app || !targetStatus) return;
    
    try {
      setSendingBack(app.monday_item_id);
      
      // Change status - Monday.com automation will move the item to the correct group
      await updateWorkflowItemStatus(app.monday_item_id, targetStatus);
      
      // Remove from local state (it will move out of Credit Approved groups via Monday automation)
      setReadyForDocsItems(prev => prev.filter(item => item.monday_item_id !== app.monday_item_id));
      setFundedApps(prev => prev.filter(item => item.monday_item_id !== app.monday_item_id));
      setFilteredReadyForDocs(prev => prev.filter(item => item.monday_item_id !== app.monday_item_id));
      setFilteredFunded(prev => prev.filter(item => item.monday_item_id !== app.monday_item_id));
      
      // Add to activity feed
      addActivity({
        type: 'item_sent_back',
        itemId: app.monday_item_id,
        sfNumber: app.sf_number,
        itemName: app.name,
        newStatus: targetStatusName,
        source: 'portal'
      });
      
      toast.success(
        <div className="flex items-center gap-2">
          <span className="font-medium">{app.sf_number}</span>
          <span>→</span>
          <span className="font-medium" style={{ color: SEND_BACK_STATUSES.find(s => s.id === targetStatus)?.color }}>{targetStatusName}</span>
        </div>
      );
    } catch (error) {
      console.error("Failed to send back item:", error);
      toast.error("Failed to change status");
    } finally {
      setSendingBack(null);
    }
  };

  // Load Monday.com updates for a specific item
  const loadMondayUpdates = async (itemId) => {
    if (!itemId) return;
    
    try {
      setMondayUpdatesLoading(true);
      setMondayUpdates([]);
      setMondayColumns({});
      
      const data = await getMondayItemUpdates(itemId);
      setMondayUpdates(data.updates || []);
      setMondayColumns(data.columns || {});
    } catch (error) {
      console.error("Failed to load Monday.com updates:", error);
      toast.error("Failed to load updates from Monday.com");
    } finally {
      setMondayUpdatesLoading(false);
    }
  };

  // Open item modal and load updates
  const openItemModal = (app) => {
    setSelectedItem(app);
    setShowItemModal(true);
    setModalTab("details");
    if (app.monday_item_id) {
      loadMondayUpdates(app.monday_item_id);
    }
  };

  // For users who can assign to anyone - open the dialog
  const handleOpenAssignDialog = (app, e, type = 'funding_specialist') => {
    e.stopPropagation();
    setSelectedAppForAssign(app);
    setSelectedAssignee("");
    setAssignmentType(type);
    setShowAssignDialog(true);
  };

  // Apply a successful assignment to the local lists.
  // NB: the funded list state is `fundedApps` — a previous version called a
  // non-existent setFundedItems here, which threw AFTER the API succeeded and
  // made the catch block misreport every successful assign as a "connection
  // blip" (and left the dialog open). That was the recurring assign bug.
  const applyAssignmentSuccess = (itemId, name) => {
    const updateItems = (items) => items.map(item =>
      item.monday_item_id === itemId ? { ...item, funding_specialist: name } : item
    );
    setReadyForDocsItems(updateItems);
    setFundedApps(updateItems);
    setFilteredReadyForDocs(updateItems);
    setFilteredFunded(updateItems);
    toast.success(`Funding Specialist assigned: ${name}`);
  };

  // When the POST response is lost in transit, check the source of truth —
  // the write usually landed and we shouldn't scare the user with an error.
  const verifyAssignmentSaved = async (itemId, name) => {
    try {
      await new Promise((r) => setTimeout(r, 2000));
      const res = await getFundingSpecialist(itemId);
      return res?.funding_specialist === name;
    } catch {
      return false;
    }
  };

  // Assign to selected team member (funding specialist)
  const handleAssignToMember = async () => {
    if (!selectedAssignee || !selectedAppForAssign) {
      toast.error("Please select a team member");
      return;
    }
    
    try {
      setAssigning(selectedAppForAssign.monday_item_id);
      
      // Assign funding specialist
      await assignFundingSpecialist(selectedAppForAssign.monday_item_id, selectedAssignee);
      applyAssignmentSuccess(selectedAppForAssign.monday_item_id, selectedAssignee);
      setShowAssignDialog(false);
      setSelectedAppForAssign(null);
      setAssignmentType(null);
    } catch (error) {
      console.error("Assignment failed:", error);
      if (!error.response && await verifyAssignmentSaved(selectedAppForAssign.monday_item_id, selectedAssignee)) {
        applyAssignmentSuccess(selectedAppForAssign.monday_item_id, selectedAssignee);
        setShowAssignDialog(false);
        setSelectedAppForAssign(null);
        setAssignmentType(null);
      } else {
        toast.error(!error.response
          ? "Connection blip while assigning — it may still have saved and will show on the next refresh."
          : (error.response?.data?.detail || "Failed to assign funding specialist"));
      }
    } finally {
      setAssigning(null);
    }
  };

  // For users who can only assign to themselves (funding specialist)
  const handleAssignToMe = async (app, e, type = 'funding_specialist') => {
    e.stopPropagation();
    
    if (!user?.name) {
      toast.error("Please log in to assign");
      return;
    }
    
    try {
      setAssigning(app.monday_item_id);
      
      // Assign funding specialist
      await assignFundingSpecialist(app.monday_item_id, user.name);
      applyAssignmentSuccess(app.monday_item_id, user.name);
    } catch (error) {
      console.error("Assignment failed:", error);
      if (!error.response && await verifyAssignmentSaved(app.monday_item_id, user.name)) {
        applyAssignmentSuccess(app.monday_item_id, user.name);
      } else {
        toast.error(!error.response
          ? "Connection blip while assigning — it may still have saved and will show on the next refresh."
          : (error.response?.data?.detail || "Failed to assign funding specialist"));
      }
    } finally {
      setAssigning(null);
    }
  };

  // Calculate totals for Ready for Doc's
  const totalReadyForDocs = readyForDocsItems.length;
  const totalReadyForDocsValue = readyForDocsItems.reduce((sum, item) => sum + (item.loan_amount || 0), 0);
  
  // Check if user can assign anyone
  const userCanAssign = canAssignAnyone && canAssignAnyone();

  // Month names for tabs
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <div data-testid="credit-approved-deals-page">
      <PageHeader
        title="Credit Approved Deals"
        description={`Real-time view of Ready for Doc's and Funded applications${isAus ? " — Australia" : ""}`}
      >
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {/* WebSocket Status */}
          <span 
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              isConnected 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}
            title={isConnected ? `Live - ${connectionCount} connected` : 'Disconnected - Reconnecting...'}
          >
            {isConnected ? (
              <>
                <Wifi className="w-3 h-3" />
                Live
              </>
            ) : (
              <>
                <WifiOff className="w-3 h-3" />
                Offline
              </>
            )}
          </span>
          
          {/* Activity Bell */}
          <Popover open={showActivityPanel} onOpenChange={(open) => {
            setShowActivityPanel(open);
            if (open) setUnreadActivityCount(0);
          }}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="relative"
                data-testid="activity-bell"
              >
                <Bell className="w-4 h-4" />
                {unreadActivityCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {unreadActivityCount > 99 ? '99+' : unreadActivityCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-0" align="end">
              <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold">Live Activity</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setActivityFeed([])}
                >
                  Clear All
                </Button>
              </div>
              <ScrollArea className="h-80">
                {activityFeed.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <Bell className="w-8 h-8 mb-2 opacity-50" />
                    <p className="text-sm">No recent activity</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {activityFeed.map((activity) => (
                      <div key={activity.id} className="px-4 py-3 hover:bg-slate-50 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                            <ArrowRight className="w-4 h-4 text-pink-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {activity.sfNumber || activity.itemName}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {activity.fromGroup !== activity.toGroup ? (
                                <>
                                  Moved from <span className="font-medium">{activity.fromGroup}</span> to <span className="font-medium text-pink-600">{activity.toGroup}</span>
                                </>
                              ) : (
                                <>
                                  Updated in <span className="font-medium text-pink-600">{activity.toGroup}</span>
                                </>
                              )}
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {new Date(activity.timestamp).toLocaleTimeString("en-GB")}
                              {activity.source === 'monday' && (
                                <span className="ml-2 text-blue-500">via Monday.com</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>
          
          <Button
            onClick={handleSync}
            disabled={syncing}
            data-testid="sync-monday-btn"
            style={{ backgroundColor: SF_BLUE }}
            className="hover:opacity-90"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sync Monday
          </Button>

          <CheckBalanceButton />
        </div>
      </PageHeader>

      {/* Compact quick stats — replaces the old three big cards */}
      <div className="hidden md:flex flex-wrap items-center gap-2 -mt-4 mb-6" data-testid="header-quick-stats">
        <div className="px-2.5 py-1 rounded-md border bg-white text-xs whitespace-nowrap" data-testid="stat-ready-docs">
          <span className="font-bold" style={{ color: CREDIT_APPROVED_GROUPS["new_group_mkmkc789"].color }}>{totalReadyForDocs}</span>
          <span className="text-muted-foreground"> Ready · {curSym}{totalReadyForDocsValue.toLocaleString()}</span>
        </div>
        <div className="px-2.5 py-1 rounded-md border bg-white text-xs whitespace-nowrap" data-testid="stat-funded-count">
          <span className="font-bold text-purple-600">{fundedStats.total_count}</span>
          <span className="text-muted-foreground"> Funded {selectedFundedYear}{selectedFundedMonth ? ` (${monthNames[selectedFundedMonth - 1]})` : ''} · New {fundedStats.new_deals} / TU {fundedStats.topups}</span>
        </div>
        <div className="px-2.5 py-1 rounded-md border bg-white text-xs whitespace-nowrap" data-testid="stat-funded-value">
          <span className="font-bold">{curSym}{fundedStats.total_amount.toLocaleString()}</span>
          <span className="text-muted-foreground"> funded</span>
        </div>
        {/* Sales ask (Sep 2026): cost of sales (broker comms) + cost of funding */}
        {fundedStats.sales && (
          <>
            <CostOfSalesHover region={isAus ? "AUS" : "UK"} curSym={curSym}>
            <div className="px-2.5 py-1 rounded-md border bg-amber-50 border-amber-200 text-xs whitespace-nowrap cursor-help" data-testid="stat-cost-of-sales"
              title={`Broker commissions on funded deals ÷ total funded${fundedStats.sales.comms_missing_count ? ` · ${fundedStats.sales.comms_missing_count} deal(s) have no commission recorded — click a deal to add it` : ""}`}>
              <span className="font-bold text-amber-800">{curSym}{Math.round(fundedStats.sales.total_comms).toLocaleString()}</span>
              <span className="text-muted-foreground"> comms · {fundedStats.sales.comms_pct}%</span>
              {fundedStats.sales.comms_missing_count > 0 && <span className="text-amber-700"> · {fundedStats.sales.comms_missing_count} missing</span>}
            </div>
            </CostOfSalesHover>
            <CostOfFundingHover region={isAus ? "AUS" : "UK"} curSym={curSym}>
            <div className="px-2.5 py-1 rounded-md border bg-sky-50 border-sky-200 text-xs whitespace-nowrap cursor-help" data-testid="stat-cost-of-funding"
              title={`Nominal test payments + final disbursements actually sent to customers (Credit Approved Deals → Process)${fundedStats.sales.cost_of_funding_missing_count ? ` · ${fundedStats.sales.cost_of_funding_missing_count} deal(s) not processed in the portal — click a deal to enter the amount sent` : ""}`}>
              <span className="font-bold text-sky-800">{curSym}{Math.round(fundedStats.sales.cost_of_funding).toLocaleString()}</span>
              <span className="text-muted-foreground"> cost of funding</span>
              {fundedStats.sales.cost_of_funding_missing_count > 0 && <span className="text-sky-700"> · {fundedStats.sales.cost_of_funding_missing_count} not processed</span>}
            </div>
            </CostOfFundingHover>
          </>
        )}
      </div>

      {/* Ready for Doc's Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5" style={{ color: CREDIT_APPROVED_GROUPS["new_group_mkmkc789"].color }} />
            Ready for Doc&apos;s
            <span className="text-sm font-normal text-muted-foreground">({filteredReadyForDocs.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground font-mono tabular-nums" data-testid="ready-docs-countdown">
              next pull {refreshCountdown}s
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { quickRefreshReadyForDocs(false); setRefreshCountdown(AUTO_REFRESH_INTERVAL); }}
              disabled={quickRefreshing}
              className="gap-1 h-7"
              data-testid="quick-refresh-ready-docs-btn"
              title="Refresh just this group from Monday (fast)"
            >
              <RefreshCw className={`w-3 h-3 ${quickRefreshing ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: SF_BLUE }} />
          </div>
        ) : filteredReadyForDocs.length === 0 ? (
          <div className="empty-state">
            <FileSearch className="empty-state-icon" style={{ color: SF_BLUE }} />
            <h3 className="text-lg font-semibold mb-2">No Items</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm 
                ? "No deals match your search criteria" 
                : "No items in Ready for Doc's group"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredReadyForDocs.map((app) => (
              <ItemCard 
                key={app.monday_item_id} 
                app={app} 
                isFunded={false}
                onOpenModal={openItemModal}
                onAssign={handleAssignToMe}
                onOpenAssignDialog={handleOpenAssignDialog}
                onSendBack={handleSendBack}
                sendingBack={sendingBack}
                canAssignAnyone={userCanAssign}
                user={user}
                assigning={assigning}
                navigate={navigate}
                envelopeSummary={envelopeSummaries[app.sf_number || app.application_id] || null}
                onLaunchWizard={setWizardDealId}
                currency={curSym}
                region={currentRegion}
              />
            ))}
          </div>
        )}
      </div>

      {/* DocuSign envelope status — table between Ready for Doc's and Funded */}
      <div className="mt-8 mb-8">
        <DocuSignEnvelopesPanel
          envelopes={docusignEnvelopes}
          loading={loadingEnvelopes}
          error={envelopesError}
          onRefresh={loadDocuSignEnvelopes}
          defaultExpanded
          compact
          onExpandChange={(open) => {
            if (open && !envelopesLoaded && !loadingEnvelopes) loadDocuSignEnvelopes();
          }}
        />
      </div>

      {/* Funded Section - With Year and Monthly Tabs */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CheckCircle className="w-5 h-5" style={{ color: CREDIT_APPROVED_GROUPS["new_group55967__1"].color }} />
            Funded Applications
            <span className="text-sm font-normal text-muted-foreground">({filteredFunded.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            {/* Year Selector */}
            <Select value={selectedFundedYear?.toString()} onValueChange={(val) => handleYearSelect(parseInt(val))}>
              <SelectTrigger className="w-[100px] h-8 text-xs" data-testid="year-selector">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center" data-testid="funded-sync-group">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSyncFunded("auto")}
                disabled={syncing}
                className="gap-1 rounded-r-none"
                title={fundedSyncState?.last_sync_at
                  ? `Pulls only what changed in Monday since the last sync (${new Date(fundedSyncState.last_sync_at).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}). Runs by itself every ${fundedSyncState.auto_every_min} min; portal disbursements are stored the moment they fund.`
                  : "Pulls what changed in Monday since the last sync; runs by itself every 15 min"}
                data-testid="sync-funded-btn"
              >
                {syncing ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3" />
                )}
                Sync Funded
                {fundedSyncState?.last_sync_at && !syncing && (
                  <span className="font-mono text-[10px] text-muted-foreground" data-testid="funded-sync-last">
                    · {new Date(fundedSyncState.last_sync_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                    {fundedSyncState.last_trigger === "scheduler" ? " auto" : ""}
                  </span>
                )}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="px-1.5 rounded-l-none border-l-0" disabled={syncing} data-testid="sync-funded-menu-btn" aria-label="Sync options">
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="text-xs">
                  <DropdownMenuItem onClick={() => handleSyncFunded("full")} data-testid="sync-funded-full">
                    Full re-sync — walk every deal in the Monday Funded group{fundedSyncState?.mirror_total ? ` (${fundedSyncState.mirror_total} mirrored)` : ""}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBackfillCoF}
              disabled={backfilling}
              className="gap-1"
              title={isAus ? "Recalculate cost of funding for every AUS deal funded since 1 Aug 2026 — portal disbursement where processed, otherwise the LoanDisk ledger (principal less the Renewal settlement of the previous loan)"
                           : "Recalculate cost of funding (nominal + final disbursement) for every deal funded since 1 Aug 2026 in this region"}
              data-testid="backfill-cost-of-funding-btn"
            >
              {backfilling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Banknote className="w-3 h-3" />}
              Recalc cost of funding
            </Button>
          </div>
        </div>
        
        {/* Monthly Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => handleMonthSelect(null)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              selectedFundedMonth === null 
                ? 'bg-purple-100 border-purple-300 text-purple-700 font-medium' 
                : 'bg-white border-slate-200 hover:bg-slate-50'
            }`}
          >
            All {selectedFundedYear}
          </button>
          {fundedMonthlySummary.map((monthData) => (
            <button
              key={monthData.month}
              onClick={() => handleMonthSelect(monthData.month)}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                selectedFundedMonth === monthData.month 
                  ? 'bg-purple-100 border-purple-300 text-purple-700 font-medium' 
                  : 'bg-white border-slate-200 hover:bg-slate-50'
              }`}
            >
              {monthNames[monthData.month - 1]} ({monthData.count})
            </button>
          ))}
        </div>
        
        {/* Search within Funded */}
        <div className="relative max-w-md mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search funded applications..."
            value={fundedSearchQuery}
            onChange={(e) => setFundedSearchQuery(e.target.value)}
            className="pl-10 h-9"
          />
          {fundedSearchQuery && (
            <button
              onClick={() => setFundedSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {fundedLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: CREDIT_APPROVED_GROUPS["new_group55967__1"].color }} />
          </div>
        ) : filteredFunded.length === 0 ? (
          <Card className="bg-slate-50/50 border-slate-200">
            <CardContent className="py-8 text-center">
              <CheckCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              {fundedSearchQuery ? (
                <p className="text-sm text-muted-foreground">No results found</p>
              ) : (
                <p className="text-sm text-muted-foreground">No funded applications for this period</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left px-3 py-2 font-medium">Application</th>
                  <th className="text-left px-3 py-2 font-medium">Company</th>
                  <th className="text-left px-3 py-2 font-medium">Type</th>
                  <th className="text-left px-3 py-2 font-medium">Broker</th>
                  <th className="text-right px-3 py-2 font-medium">Amount</th>
                  <th className="text-right px-3 py-2 font-medium" title="Broker commission (from Monday; click to correct)">Comms</th>
                  <th className="text-right px-3 py-2 font-medium" title="Net money disbursed (cost of funding)">Cost of funding</th>
                  <th className="text-left px-3 py-2 font-medium">Funded Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredFunded.map((app) => {
                  const isTopUp = app.company_name?.includes('(TU)');
                  return (
                    <tr 
                      key={app.id || app.application_id} 
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => {
                        // Navigate to workflow if we have the ID
                        const appId = app.application_id || app.id;
                        if (appId) navigate(`/workflow/${appId}`);
                      }}
                    >
                      <td className="px-3 py-2 font-mono text-xs">{app.application_id}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[200px]">
                            {app.company_name?.replace(' (TU)', '')}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {isTopUp ? (
                          <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold text-purple-800">
                            Top Up
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold text-white" style={{ backgroundColor: SF_BLUE }}>
                            New
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground truncate max-w-[150px]">
                        {app.broker_name || 'Direct'}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-purple-600">
                        {curSym}{(app.loan_amount || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right text-xs" onClick={(e) => e.stopPropagation()}>
                        <SalesMetricCell app={app} field="commission_amount" curSym={curSym} onSaved={() => loadFundedApplications(selectedFundedYear, selectedFundedMonth)} />
                      </td>
                      <td className="px-3 py-2 text-right text-xs" onClick={(e) => e.stopPropagation()}>
                        <SalesMetricCell app={app} field="cost_of_funding" curSym={curSym} onSaved={() => loadFundedApplications(selectedFundedYear, selectedFundedMonth)} />
                      </td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">
                        {app.funded_date ? new Date(app.funded_date).toLocaleDateString('en-GB') : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Summary Footer */}
            <div className="flex items-center justify-between px-3 py-3 bg-slate-50 border-t mt-2 rounded-b-lg">
              <div className="flex items-center gap-4 text-xs">
                <span className="text-muted-foreground">
                  Total: <span className="font-semibold text-foreground">{filteredFunded.length}</span> applications
                </span>
                <span className="text-muted-foreground">
                  New: <span className="font-semibold" style={{ color: SF_BLUE }}>{fundedStats.new_deals}</span>
                </span>
                <span className="text-muted-foreground">
                  Top Ups: <span className="font-semibold text-purple-600">{fundedStats.topups}</span>
                </span>
              </div>
              <div className="text-sm font-bold text-purple-600">
                Total: {curSym}{fundedStats.total_amount.toLocaleString()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={(open) => {
        setShowAssignDialog(open);
        if (!open) setAssignmentType(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-pink-500" />
              Assign Funding Specialist
            </DialogTitle>
            <DialogDescription>
              Assign a funding specialist to {selectedAppForAssign?.sf_number || selectedAppForAssign?.application_id} ({selectedAppForAssign?.name?.replace(/^SFA?-\d+\s*-?\s*/, '') || selectedAppForAssign?.company_name})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Funding Specialist</label>
              <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                <SelectTrigger data-testid="assign-select-member">
                  <SelectValue placeholder="Choose a team member..." />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.map((member) => (
                    <SelectItem key={member.id} value={member.name}>
                      <div className="flex items-center gap-2">
                        <span>{member.name}</span>
                        <span className="text-xs text-muted-foreground">({member.role})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignToMember}
              disabled={!selectedAssignee || assigning}
              className="bg-pink-600 hover:bg-pink-700"
              data-testid="confirm-assign-btn"
            >
              {assigning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                "Assign"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Detail Modal */}
      <Dialog open={showItemModal} onOpenChange={(open) => {
        setShowItemModal(open);
        if (!open) setModalTab("details");
      }}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" style={{ color: SF_BLUE }} />
              {selectedItem?.name?.replace(/^SFA?-\d+\s*-?\s*/, '') || selectedItem?.sf_number}
            </DialogTitle>
            <DialogDescription>
              {selectedItem?.sf_number} - {selectedItem?.group_name || "Credit Approved Deal"}
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Tab Navigation */}
              <div className="flex border-b mb-4 flex-shrink-0">
                <button
                  onClick={() => setModalTab("details")}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    modalTab === "details"
                      ? "border-pink-600 text-pink-600"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-1.5" />
                  Details
                </button>
                {selectedItem.monday_item_id && (
                  <button
                    onClick={() => setModalTab("updates")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      modalTab === "updates"
                        ? "border-pink-600 text-pink-600"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <MessageCircle className="w-4 h-4 inline mr-1.5" />
                    Updates
                    {mondayUpdates.length > 0 && (
                      <span className="ml-1.5 text-xs bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded-full">
                        {mondayUpdates.length}
                      </span>
                    )}
                  </button>
                )}
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto">
                {modalTab === "details" ? (
                  <div className="space-y-6 pr-2">
                    {/* Summary Card */}
                    <div className="bg-pink-50 rounded-lg p-4 border border-pink-200">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">SF Number</p>
                          <p className="font-mono font-semibold">{selectedItem.sf_number}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Loan Amount</p>
                          <p className="font-bold text-pink-700 text-lg">
                            {curSym}{(selectedItem.loan_amount || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Status</p>
                          <span className="inline-flex items-center rounded-full bg-pink-100 px-2 py-0.5 text-xs font-semibold text-pink-800">
                            {selectedItem.status || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Group</p>
                          <p className="font-semibold">{selectedItem.group_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">AI Opinion</p>
                          <p className="font-semibold">{selectedItem.ai_opinion || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Duration</p>
                          <p className="font-semibold">{selectedItem.duration || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Broker Information */}
                    {selectedItem.broker_name && (
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          Broker Information
                        </h4>
                        <div className="bg-slate-50 rounded-lg p-3 border">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground">Broker Name</p>
                              <p className="font-semibold">{selectedItem.broker_name}</p>
                            </div>
                            {selectedItem.commission && (
                              <div>
                                <p className="text-xs text-muted-foreground">Commission</p>
                                <p className="font-semibold">{selectedItem.commission}%</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Contact Information */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        Contact Information
                      </h4>
                      <div className="bg-slate-50 rounded-lg p-3 border">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Contact Name</p>
                            <p className="font-semibold">{selectedItem.contact_name || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Contact Email</p>
                            <p className="font-semibold">{selectedItem.contact_email || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Account Manager</p>
                            <p className="font-semibold">{selectedItem.account_manager || 'Not assigned'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Updates Tab */
                  <div className="space-y-4 pr-2">
                    {/* Monday.com Column Values */}
                    {Object.keys(mondayColumns).length > 0 && (
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <Database className="w-4 h-4 text-blue-600" />
                          Monday.com Fields
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                          {Object.entries(mondayColumns).map(([key, value]) => (
                            <div key={key} className="bg-white rounded p-2 border">
                              <p className="text-muted-foreground truncate">{key}</p>
                              <p className="font-medium truncate" title={value}>{value || '-'}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Updates List */}
                    <div>
                      <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-muted-foreground" />
                        Activity & Updates ({mondayUpdates.length})
                      </h4>
                      
                      {mondayUpdatesLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-pink-600" />
                          <span className="ml-2 text-sm text-muted-foreground">Loading updates...</span>
                        </div>
                      ) : mondayUpdates.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground bg-slate-50 rounded-lg border">
                          <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No updates found for this item</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {mondayUpdates.map((update) => (
                            <div key={update.id} className="bg-white rounded-lg border p-3 hover:shadow-sm transition-shadow">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  {update.creator?.photo ? (
                                    <img 
                                      src={update.creator.photo} 
                                      alt={update.creator.name}
                                      className="w-6 h-6 rounded-full"
                                    />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-pink-100 flex items-center justify-center">
                                      <User className="w-3 h-3 text-pink-600" />
                                    </div>
                                  )}
                                  <span className="font-medium text-sm">{update.creator?.name || 'Unknown'}</span>
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {update.created_at ? new Date(update.created_at).toLocaleString("en-GB") : ''}
                                </span>
                              </div>
                              
                              <div 
                                className="text-sm text-gray-700 prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={sanitizedHtmlProps(update.body || update.text_body || '')}
                              />
                              
                              {update.assets && update.assets.length > 0 && (
                                <div className="mt-2 pt-2 border-t">
                                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                                    <Paperclip className="w-3 h-3" />
                                    Attachments ({update.assets.length})
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {update.assets.map((asset) => (
                                      <a
                                        key={asset.id}
                                        href={asset.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                      >
                                        <Paperclip className="w-3 h-3" />
                                        {asset.name}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t mt-4 flex-shrink-0">
                {selectedItem.monday_item_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                      window.open(`https://simplyfunded.monday.com/boards/1895143857/pulses/${selectedItem.monday_item_id}`, '_blank');
                    }}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in Monday.com
                  </Button>
                )}
                <Button
                  size="sm"
                  style={{ backgroundColor: SF_BLUE }}
                  onClick={() => {
                    const appIdForWorkflow = selectedItem.sf_number || selectedItem.application_id || selectedItem.monday_item_id;
                    navigate(`/workflow/${appIdForWorkflow}`);
                  }}
                >
                  Process Deal
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowItemModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contract wizard pop-out — same Generate Documents wizard as the Onboarding Centre */}
      {wizardDealId && (
        <DocumentGeneration
          wizardOnly
          wizardDeal={wizardDealId}
          onWizardClose={() => setWizardDealId(null)}
        />
      )}
    </div>
  );
}
